import React from "react";
import { Truck, MapPin, Snowflake, Clock, ShieldCheck, FlaskConical } from "lucide-react";
import usePageMeta from "../lib/usePageMeta";

const badges = [
  { icon: MapPin, label: "UK Based" },
  { icon: Truck, label: "Same-Day Dispatch" },
  { icon: ShieldCheck, label: "Tracked Delivery" },
  { icon: Snowflake, label: "Stable for UK Transit" },
  { icon: FlaskConical, label: "Research Use Only" },
];

export default function SameDayDispatch() {
  usePageMeta("Same-Day UK Dispatch", "Fast UK dispatch for research-use-only laboratory material.");
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="dispatch-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-7">
          <div className="pg-micro mb-3">// Same-Day UK Dispatch</div>
          <h1 className="text-[#F7FAFC] text-4xl lg:text-5xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            Fast UK dispatch for research-use-only laboratory material.
          </h1>
          <p className="text-[#9CA8B8] mt-5 leading-relaxed text-lg">
            Peptigen Labs holds selected research compounds in UK stock for fast domestic dispatch.
            Eligible orders placed before the daily cut-off are dispatched the same working day.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {badges.map(({ icon: Icon, label }) => (
              <span key={label} className="pg-badge-cyan">
                <Icon size={11} /> {label}
              </span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="pg-card p-8 relative" data-testid="cutoff-panel">
            <div className="absolute -top-3 left-6 px-3 py-1 bg-[#0B1220] border border-[#A6FF00] rounded-sm">
              <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#A6FF00]">Cut-off</span>
            </div>
            <Clock size={28} className="text-[#A6FF00] mb-4" />
            <div className="pg-display text-6xl text-[#F7FAFC] mb-2" style={{ fontFamily: "'Exo 2'" }}>14:00</div>
            <div className="pg-mono text-xs uppercase tracking-[0.22em] text-[#9CA8B8] mb-6">GMT · Working Days</div>
            <p className="text-[#9CA8B8] text-sm leading-relaxed">
              Order before 14:00 for same-day UK dispatch. Orders placed after the cut-off, on
              weekends, or on UK bank holidays will be processed the next working day.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="pg-card p-6">
          <Truck size={20} className="text-[#00C2D6] mb-3" />
          <h3 className="text-[#F7FAFC] pg-heading text-lg mb-2" style={{ fontFamily: "'Rajdhani'" }}>UK-Only Dispatch</h3>
          <p className="text-[#9CA8B8] text-sm leading-relaxed">
            Tracked UK delivery on every order. We dispatch from a British facility to UK
            researchers and laboratories.
          </p>
        </div>
        <div className="pg-card p-6">
          <Snowflake size={20} className="text-[#00C2D6] mb-3" />
          <h3 className="text-[#F7FAFC] pg-heading text-lg mb-2" style={{ fontFamily: "'Rajdhani'" }}>Stable for UK Transit</h3>
          <p className="text-[#9CA8B8] text-sm leading-relaxed">
            Lyophilised material remains stable at ambient temperature for short UK transit
            in sealed, padded packaging. Refrigerate at 2–8 °C on receipt.
          </p>
        </div>
        <div className="pg-card p-6">
          <ShieldCheck size={20} className="text-[#00C2D6] mb-3" />
          <h3 className="text-[#F7FAFC] pg-heading text-lg mb-2" style={{ fontFamily: "'Rajdhani'" }}>Tracking Provided</h3>
          <p className="text-[#9CA8B8] text-sm leading-relaxed">
            Tracking numbers are added to each order in our admin and shared with the customer
            once dispatched.
          </p>
        </div>
      </div>

      <div className="mt-12 p-6 border border-[#A6FF00]/30 bg-[#A6FF00]/[0.04] rounded-sm" data-testid="dispatch-compliance">
        <div className="pg-micro text-[#A6FF00] mb-2">// Compliance</div>
        <p className="text-[#9CA8B8] text-sm leading-relaxed max-w-4xl">
          We use the term "same-day dispatch" rather than "same-day delivery". We do not promise
          delivery on the same day. All products are supplied strictly for laboratory research
          use only.
        </p>
      </div>
    </div>
  );
}
